import { useJsApiLoader } from "@react-google-maps/api";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import { apiService } from "../../services/apiService";
import "../../styles/Access.css";
import "../../styles/FleetMonitoring.css";
import { images } from "../../utils/images";
import FleetDrawer from "./FleetDrawer";
import FleetMap from "./FleetMap";
import FleetSidebar from "./FleetSidebar";

// Pakistan center coordinates
const defaultCenter = { lat: 30.3753, lng: 69.3451 };
const VEHICLES_PAGE_LIMIT = 10;

const fleetStatusTabs = [
  { label: "All", value: "all" },
  { label: "Idle", value: "IDLE" },
  { label: "Inactive", value: "INACTIVE" },
  { label: "Running", value: "RUNNING" },
  { label: "Stop", value: "STOP" },
];



const formatFleetStatusLabel = (value) => {
  if (!value) return "Inactive";
  const str = String(value).trim().toUpperCase();
  if (str === "IDLE") return "Idle";
  if (str === "INACTIVE") return "Inactive";
  if (str === "RUNNING") return "Running";
  if (str === "STOP") return "Stop";
  if (str === "MOVING" || str === "ACTIVE" || str === "AVAILABLE") return "Running";
  if (str === "STOPPED" || str === "ALERT") return "Stop";
  if (str === "RESTING" || str === "LOADING" || str === "UNLOADING" || str === "FUELING") return "Idle";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const decodePolyline = (encoded = "") => {
  let index = 0;
  let lat = 0;
  let lng = 0;
  const path = [];

  while (index < encoded.length) {
    let shift = 0;
    let result = 0;
    let byte = null;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20 && index < encoded.length);

    lat += result & 1 ? ~(result >> 1) : result >> 1;
    shift = 0;
    result = 0;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20 && index < encoded.length);

    lng += result & 1 ? ~(result >> 1) : result >> 1;
    path.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }

  return path.filter(
    (point) => Number.isFinite(point.lat) && Number.isFinite(point.lng),
  );
};

const FleetMonitoring = ({ showHeader = true }) => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [trackedVehicles, setTrackedVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [vehiclesMeta, setVehiclesMeta] = useState({});
  const [vehiclesPage, setVehiclesPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [isTracked, setIsTracked] = useState(false);
  const [activeMarker, setActiveMarker] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerVehicle, setDrawerVehicle] = useState(null);
  const [drawerDetail, setDrawerDetail] = useState(null);
  const [drawerDetailLoading, setDrawerDetailLoading] = useState(false);
  const [deviceTypeTab, setDeviceTypeTab] = useState("tracker");
  const [trackButtonStatus, setTrackButtonStatus] = useState("idle");
  const fetchRequestRef = useRef(0);
  const trackButtonStatusTimerRef = useRef(null);

  const queueTrackButtonStatusReset = () => {
    if (trackButtonStatusTimerRef.current) {
      clearTimeout(trackButtonStatusTimerRef.current);
    }

    trackButtonStatusTimerRef.current = setTimeout(() => {
      setTrackButtonStatus("idle");
      trackButtonStatusTimerRef.current = null;
    }, 1600);
  };

  useEffect(() => {
    return () => {
      if (trackButtonStatusTimerRef.current) {
        clearTimeout(trackButtonStatusTimerRef.current);
      }
    };
  }, []);

  const mapApiStatus = useCallback((item, currentDetail) => {
    const rawStatus =
      item?.last_log?.status ||
      currentDetail?.status ||
      item?.status ||
      item?.vehicle_status ||
      currentDetail?.vehicle_status ||
      "INACTIVE";

    const statusLabel = formatFleetStatusLabel(rawStatus);
    const statusKey = statusLabel.toLowerCase();

    return {
      statusKey,
      statusLabel,
    };
  }, []);

  const mapVehicleItem = useCallback(
    (item) => {
      try {
        if (!item) return null;
        const currentDetail =
          item.last_log ||
          item.vehicle_current_detail ||
          item.fmcr_trip?.trip_current_detail ||
          item.current_info ||
          {};

        const lat = Number(
          currentDetail?.lat ??
            item.lat ??
            item.fmcr_trip?.current_lat ??
            defaultCenter.lat,
        );
        const lng = Number(
          currentDetail?.lng ??
            item.lng ??
            item.fmcr_trip?.current_lng ??
            defaultCenter.lng,
        );

        const statusInfo = mapApiStatus(item, currentDetail);
        const locationName =
          currentDetail?.location ||
          item.last_log?.location ||
          item.vehicle_current_detail?.location ||
          item.location ||
          "-";

        const formattedOdm = item.odm
          ? `${Number(item.odm).toLocaleString(undefined, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })} km`
          : currentDetail?.odometer
            ? `${Number(currentDetail.odometer).toLocaleString()} km`
            : "-";

        const polylineObj = item.fmcr_trip?.polyline || item.polyline;

        // 1. Full Polyline (Planned trip route from start to end)
        const encodedFullPolyline =
          polylineObj?.full_polyline ||
          item.fmcr_trip?.full_polyline ||
          item.full_polyline ||
          "";
        const fullRoute = encodedFullPolyline
          ? decodePolyline(encodedFullPolyline)
          : [];

        // 2. Traveled Polyline (Live traveled path updated by cron)
        const encodedTraveledPolyline =
          (typeof polylineObj === "string"
            ? polylineObj
            : polylineObj?.polyline) ||
          item.fmcr_trip?.polyline_traveled ||
          item.polyline_traveled ||
          "";
        const traveledRoute = encodedTraveledPolyline
          ? decodePolyline(encodedTraveledPolyline)
          : [];

        // Primary route fallback for bounds/center
        const route =
          traveledRoute.length > 0
            ? traveledRoute
            : fullRoute.length > 0
              ? fullRoute
              : [{ lat, lng }];

        // Start & End points for route markers
        const startPoint =
          item.fmcr_trip?.start_lat && item.fmcr_trip?.start_lng
            ? {
                lat: Number(item.fmcr_trip.start_lat),
                lng: Number(item.fmcr_trip.start_lng),
              }
            : fullRoute.length > 0
              ? fullRoute[0]
              : traveledRoute.length > 0
                ? traveledRoute[0]
                : null;

        const endPoint =
          item.fmcr_trip?.end_lat && item.fmcr_trip?.end_lng
            ? {
                lat: Number(item.fmcr_trip.end_lat),
                lng: Number(item.fmcr_trip.end_lng),
              }
            : fullRoute.length > 1
              ? fullRoute[fullRoute.length - 1]
              : null;

        const lastUpdatedRaw =
          item.last_log?.created_at ||
          currentDetail?.created_at ||
          item.last_log?.last_updated ||
          currentDetail?.last_updated ||
          currentDetail?.updated_at ||
          item.last_log?.updated_at ||
          item.created_at ||
          item.last_updated ||
          item.updated_at ||
          item.vehicle_current_detail?.created_at ||
          item.vehicle_current_detail?.last_updated ||
          null;

        const regNumber =
          item.registration_number ||
          item.registration_no ||
          item.reg_no ||
          item.vehicle_number ||
          item.vehicle_no ||
          item.plate_number ||
          item.vehicle?.registration_number ||
          item.vehicle?.registration_no ||
          item.name ||
          item.title ||
          (item.id ? `Vehicle #${item.id}` : "-");

        const tripIdStr = item.fmcr_trip?.id
          ? `TR-${item.fmcr_trip.id}`
          : item.trip_id
            ? `TR-${item.trip_id}`
            : item.trip?.id
              ? `TR-${item.trip.id}`
              : "-";

        const speedVal =
          currentDetail?.speed ??
          item.last_log?.speed ??
          item.speed ??
          0;

        const driverName =
          item.primary_driver?.username ||
          item.primary_driver?.name ||
          item.driver?.name ||
          item.driver_name ||
          (typeof item.driver === "string" ? item.driver : "") ||
          "Unassigned";

        const driverPhone =
          item.primary_driver?.mobile ||
          item.primary_driver?.phone ||
          item.driver?.mobile ||
          item.driver_mobile ||
          item.phone ||
          "-";

        const driverImg =
          item.primary_driver?.image ||
          item.driver?.image ||
          item.driverImage ||
          "";

        return {
          id: item.id || item.vehicle_id || Math.random(),
          regNumber,
          tripId: tripIdStr,
          vehicle_status: item.vehicle_status,
          status: statusInfo.statusLabel,
          statusKey: statusInfo.statusKey,
          lat,
          lng,
          location: locationName,
          speed: `${speedVal} km/h`,
          driver: driverName,
          driverImage: driverImg,
          phone: driverPhone,
          battery: "80%",
          fuel: item.current_info?.fuel_volume
            ? `${Number(item.current_info.fuel_volume)}L`
            : item.current_fuel
              ? `${Number(item.current_fuel)}L`
              : item.fuel_level
                ? `${item.fuel_level}L`
                : "-",
          avatarIdx: Math.abs((Number(item.id) || 0) % 4),
          odo: formattedOdm,
          lastUpdated: lastUpdatedRaw,
          fullRoute,
          traveledRoute,
          route,
          startPoint,
          endPoint,
          imeino:
            item.imeino ||
            item.imei ||
            currentDetail?.imeino ||
            currentDetail?.imei ||
            item.last_log?.imeino ||
            null,
          tracker_status:
            currentDetail?.tracker_status ?? item.tracker_status ?? null,
        };
      } catch (err) {
        console.error("Error in mapVehicleItem:", err, item);
        return {
          id: item?.id || Math.random(),
          regNumber: item?.registration_number || "-",
          tripId: "-",
          status: "Moving",
          statusKey: "moving",
          lat: defaultCenter.lat,
          lng: defaultCenter.lng,
          location: "-",
          speed: "0 km/h",
          driver: "Unassigned",
          driverImage: "",
          phone: "-",
          battery: "-",
          fuel: "-",
          avatarIdx: 0,
          odo: "-",
          lastUpdated: null,
          fullRoute: [],
          traveledRoute: [],
          route: [],
          startPoint: null,
          endPoint: null,
          imeino: item?.imeino || null,
          tracker_status: null,
        };
      }
    },
    [mapApiStatus],
  );

  const extractVehiclesPayload = (response) => {
    let results = [];
    let meta = {};

    if (Array.isArray(response)) {
      results = response;
    } else if (Array.isArray(response?.results)) {
      results = response.results;
      meta = response?.meta || {};
    } else if (Array.isArray(response?.data?.results)) {
      results = response.data.results;
      meta = response?.data?.meta || response?.meta || {};
    } else if (Array.isArray(response?.data?.data?.results)) {
      results = response.data.data.results;
      meta = response?.data?.data?.meta || response?.meta || {};
    } else if (Array.isArray(response?.data?.data)) {
      results = response.data.data;
      meta = response?.data?.meta || response?.meta || {};
    } else if (Array.isArray(response?.data)) {
      results = response.data;
      meta = response?.meta || {};
    } else if (Array.isArray(response?.rows)) {
      results = response.rows;
      meta = response?.meta || {};
    } else if (Array.isArray(response?.data?.rows)) {
      results = response.data.rows;
      meta = response?.data?.meta || response?.meta || {};
    } else if (Array.isArray(response?.vehicles)) {
      results = response.vehicles;
      meta = response?.meta || {};
    } else if (Array.isArray(response?.data?.vehicles)) {
      results = response.data.vehicles;
      meta = response?.data?.meta || response?.meta || {};
    }

    const total =
      Number(
        meta?.totalItems ??
          meta?.total ??
          response?.total ??
          response?.data?.total ??
          results.length,
      ) || 0;

    return {
      results,
      meta,
      total,
    };
  };

  const mergeVehiclePages = (previousVehicles, nextVehicles) => {
    const vehicleMap = new Map();

    previousVehicles.forEach((vehicle) => {
      if (vehicle && vehicle.id) {
        vehicleMap.set(vehicle.id, vehicle);
      }
    });

    nextVehicles.forEach((newVehicle) => {
      if (!newVehicle || !newVehicle.id) return;
      const existing = vehicleMap.get(newVehicle.id);
      if (existing) {
        const route = [...(existing.route || [])];
        const lastPoint = route[route.length - 1];
        if (
          !lastPoint ||
          lastPoint.lat !== newVehicle.lat ||
          lastPoint.lng !== newVehicle.lng
        ) {
          route.push({ lat: newVehicle.lat, lng: newVehicle.lng });
        }
        vehicleMap.set(newVehicle.id, { ...newVehicle, route });
        return;
      }

      vehicleMap.set(newVehicle.id, newVehicle);
    });

    return Array.from(vehicleMap.values());
  };

  const fetchVehicles = useCallback(
    async ({ page = 1, append = false } = {}) => {
      const requestId = fetchRequestRef.current + 1;
      fetchRequestRef.current = requestId;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await apiService.getFmcrVehicles({
          page,
          limit: VEHICLES_PAGE_LIMIT,
          search_text: searchText,
          status: activeTab,
        });
        if (fetchRequestRef.current !== requestId) return;

        const { results, meta, total } = extractVehiclesPayload(response);
        const mapped = results.map(mapVehicleItem).filter(Boolean);

        setVehicles((prevVehicles) => {
          return append ? mergeVehiclePages(prevVehicles, mapped) : mapped;
        });
        setVehiclesMeta({
          ...meta,
          totalItems: total,
          hasMore: results.length === VEHICLES_PAGE_LIMIT,
        });
        setVehiclesPage(page);
      } catch (error) {
        console.error("Failed to fetch vehicles:", error);
      } finally {
        if (fetchRequestRef.current === requestId) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [activeTab, mapVehicleItem, searchText],
  );

  useEffect(() => {
    setVehicles([]);
    setVehiclesMeta({});
    setVehiclesPage(1);
    setSelectedIds([]);
    setTrackedVehicles([]);
    setIsTracked(false);
    setExpandedId(null);
    setActiveMarker(null);
    fetchVehicles({ page: 1, append: false });
  }, [activeTab, searchText, fetchVehicles]);

  const handleOpenDrawer = async (vehicle) => {
    setDrawerVehicle(vehicle);
    setIsDrawerOpen(true);
    setDrawerDetail(null);
    setDrawerDetailLoading(true);
    try {
      const response = await apiService.getFmcrVehicleDetail(vehicle.id);
      if (response && response.data) {
        setDrawerDetail(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch vehicle detail:", error);
    } finally {
      setDrawerDetailLoading(false);
    }
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setDrawerVehicle(null);
    setDrawerDetail(null);
    setDrawerDetailLoading(false);
  };

  const handleToggleExpand = (id) => {
    setExpandedId((prev) => {
      const next = prev === id ? null : id;
      if (next) {
        const vehicle = vehicles.find((v) => v.id === id);
        if (vehicle) {
          if (isTracked && selectedIds.includes(id)) {
            setCenter({ lat: vehicle.lat, lng: vehicle.lng });
            setZoom(12);
            setActiveMarker(vehicle);
          }
        }
      } else {
        setActiveMarker(null);
      }
      return next;
    });
  };

  // Map Navigation & Toggle States
  const [zoom, setZoom] = useState(5);
  const [center, setCenter] = useState(defaultCenter);
  const [liveMode, setLiveMode] = useState(true);
  const [trafficMode, setTrafficMode] = useState(false);
  const [routesMode, setRoutesMode] = useState(true);

  const mapRef = useRef(null);

  // Load Google Maps API key
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: (process.env.REACT_APP_API_GOOGLE_MAP_KEY || "").trim(),
  });

  const totalVehicleItems =
    Number(vehiclesMeta?.totalItems ?? vehiclesMeta?.total) || 0;
  const totalVehiclePages =
    Number(vehiclesMeta?.totalPages) ||
    (totalVehicleItems > 0
      ? Math.ceil(totalVehicleItems / VEHICLES_PAGE_LIMIT)
      : 0);

  const hasMoreVehicles = useMemo(() => {
    if (totalVehiclePages > 0) {
      return vehiclesPage < totalVehiclePages;
    }
    if (totalVehicleItems > 0) {
      return vehicles.length < totalVehicleItems;
    }
    return Boolean(vehiclesMeta?.hasMore);
  }, [totalVehiclePages, totalVehicleItems, vehicles.length, vehiclesMeta?.hasMore, vehiclesPage]);

  // Filter checklist vehicles
  const filteredVehicles = useMemo(() => {
    return vehicles;
  }, [vehicles]);

  const handleLoadMoreVehicles = useCallback(() => {
    if (loading || loadingMore || !hasMoreVehicles) return;
    fetchVehicles({ page: vehiclesPage + 1, append: true });
  }, [fetchVehicles, hasMoreVehicles, loading, loadingMore, vehiclesPage]);

  // Track checked list
  const handleSelectVehicle = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? [] : [id]));
  };

  // Select All is hidden for now; vehicles should be tracked one at a time.
  // const handleSelectAll = (checked) => {
  //   if (checked) {
  //     const allFilteredIds = filteredVehicles.map(v => v.id);
  //     setSelectedIds(prev => Array.from(new Set([...prev, ...allFilteredIds])));
  //   } else {
  //     const allFilteredIds = filteredVehicles.map(v => v.id);
  //     setSelectedIds(prev => prev.filter(id => !allFilteredIds.includes(id)));
  //   }
  // };

  // const isAllSelected = useMemo(() => {
  //   if (filteredVehicles.length === 0) return false;
  //   return filteredVehicles.every(v => selectedIds.includes(v.id));
  // }, [filteredVehicles, selectedIds]);

  // Track button trigger
  const handleTrackSelected = async () => {
    if (selectedIds.length === 0 || trackButtonStatus === "loading") return;

    setTrackButtonStatus("loading");
    try {
      const response = await apiService.getFmcrVehiclesView({
        vehicle_Ids: selectedIds,
      });
      const apiData = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];

      const mapped = apiData.map(mapVehicleItem);

      // Merge routes: preserve planned fullRoute and live updated traveledRoute using Lodash
      const updatedMapped = _.map(mapped, (newV) => {
        const existing = _.find(vehicles, { id: newV.id });
        if (existing) {
          const fullRoute = !_.isEmpty(newV.fullRoute)
            ? newV.fullRoute
            : existing.fullRoute || [];

          let traveledRoute = !_.isEmpty(newV.traveledRoute)
            ? newV.traveledRoute
            : [...(existing.traveledRoute || [])];

          const lastPoint = _.last(traveledRoute);
          if (
            !lastPoint ||
            lastPoint.lat !== newV.lat ||
            lastPoint.lng !== newV.lng
          ) {
            traveledRoute.push({ lat: newV.lat, lng: newV.lng });
          }

          const route = !_.isEmpty(traveledRoute)
            ? traveledRoute
            : !_.isEmpty(fullRoute)
              ? fullRoute
              : [{ lat: newV.lat, lng: newV.lng }];

          const startPoint =
            newV.startPoint ||
            existing.startPoint ||
            _.first(fullRoute) ||
            null;
          const endPoint =
            newV.endPoint || existing.endPoint || _.last(fullRoute) || null;

          return {
            ...newV,
            fullRoute,
            traveledRoute,
            route,
            startPoint,
            endPoint,
          };
        }
        return newV;
      });

      setTrackedVehicles(updatedMapped);
      setIsTracked(true);
      setTrackButtonStatus("success");

      // Zoom/Center on the tracked vehicles
      if (updatedMapped.length === 1) {
        setCenter({ lat: updatedMapped[0].lat, lng: updatedMapped[0].lng });
        setZoom(10);
      } else if (updatedMapped.length > 1) {
        if (mapRef.current) {
          const bounds = new window.google.maps.LatLngBounds();
          updatedMapped.forEach((v) =>
            bounds.extend({ lat: v.lat, lng: v.lng }),
          );
          mapRef.current.fitBounds(bounds);
        } else {
          // Fallback center
          setCenter({ lat: updatedMapped[0].lat, lng: updatedMapped[0].lng });
          setZoom(6);
        }
      }
    } catch (error) {
      console.error("Failed to track selected vehicles:", error);
      setTrackButtonStatus("error");
    } finally {
      queueTrackButtonStatusReset();
    }
  };

  // Map onLoad callback
  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Map onUnmount
  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  // Float Controls Callbacks
  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 1, 20));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 1, 1));
  const handleReset = () => {
    setCenter(defaultCenter);
    setZoom(5);
    setSelectedIds([]);
    setIsTracked(false);
    setTrackedVehicles([]);
    setActiveMarker(null);
    setLiveMode(false);
    setTrafficMode(false);
    setRoutesMode(true);
  };

  // Auto-hide map tracking overlay if no vehicles are checked
  useEffect(() => {
    if (selectedIds.length === 0) {
      setIsTracked(false);
      setActiveMarker(null);
    }
  }, [selectedIds]);

  // Live Mode: Real API polling every 10 seconds
  useEffect(() => {
    if (!liveMode || !isTracked || selectedIds.length === 0) return;

    const interval = setInterval(async () => {
      try {
        const response = await apiService.getFmcrVehiclesView({
          vehicle_Ids: selectedIds,
        });
        const apiData = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        const mapped = apiData.map(mapVehicleItem);

        setTrackedVehicles((prevVehicles) =>
          mapped.map((newV) => {
            const existing = prevVehicles.find((v) => v.id === newV.id);
            if (existing) {
              const fullRoute =
                newV.fullRoute && newV.fullRoute.length > 0
                  ? newV.fullRoute
                  : existing.fullRoute || [];
              let traveledRoute =
                newV.traveledRoute && newV.traveledRoute.length > 0
                  ? newV.traveledRoute
                  : [...(existing.traveledRoute || [])];
              const lastPoint = traveledRoute[traveledRoute.length - 1];
              if (
                !lastPoint ||
                lastPoint.lat !== newV.lat ||
                lastPoint.lng !== newV.lng
              ) {
                traveledRoute.push({ lat: newV.lat, lng: newV.lng });
              }
              const route =
                traveledRoute.length > 0
                  ? traveledRoute
                  : fullRoute.length > 0
                    ? fullRoute
                    : [{ lat: newV.lat, lng: newV.lng }];
              const startPoint = newV.startPoint || existing.startPoint || null;
              const endPoint = newV.endPoint || existing.endPoint || null;
              return {
                ...newV,
                fullRoute,
                traveledRoute,
                route,
                startPoint,
                endPoint,
              };
            }
            return newV;
          }),
        );
      } catch (err) {
        console.error("Live mode polling error:", err);
      }
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [liveMode, isTracked, selectedIds, mapVehicleItem]);
  const handleCenterOnVehicle = (vehicle) => {
    if (!vehicle || isNaN(vehicle.lat) || isNaN(vehicle.lng)) return;
    setCenter({ lat: vehicle.lat, lng: vehicle.lng });
    setZoom(12);
    if (isTracked && selectedIds.includes(vehicle.id)) {
      setActiveMarker(vehicle);
    }
  };

  // Marker icon builder (Vehicle / Truck Icon)
  const getMarkerIcon = (statusKey) => {
    if (!window.google) return null;
    return {
      url: images.vehcileImg || images.fuelTruck,
      scaledSize: new window.google.maps.Size(38, 38),
      anchor: new window.google.maps.Point(19, 19),
    };
  };

  return (
    <div className="fleet_monitoring_page">
      {/* Top Header Section */}
      {showHeader && <Breadcrumbs values={["FMCR", "Fleet Monitoring"]} />}

      {showHeader && (
        <div className="access_view">
          <div className="d-flex align-items-center gap-3">
            <h2>FMCR</h2>

            <div className="access_btn">
              <button
                className="active"
                onClick={() => navigate("/fmcr/fleet-monitoring")}
              >
                Tracking
              </button>
              <button className="" onClick={() => navigate("/fmcr")}>
                Reports
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Body Layout */}
      <div className="fleet_monitoring_content">
        {/* Left Sidebar Checklist */}
        <FleetSidebar
          loading={loading}
          loadingMore={loadingMore}
          searchText={searchText}
          setSearchText={setSearchText}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          statusTabs={fleetStatusTabs}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          filteredVehicles={filteredVehicles}
          hasMoreVehicles={hasMoreVehicles}
          onLoadMoreVehicles={handleLoadMoreVehicles}
          handleSelectVehicle={handleSelectVehicle}
          handleTrackSelected={handleTrackSelected}
          trackButtonStatus={trackButtonStatus}
          expandedId={expandedId}
          handleToggleExpand={handleToggleExpand}
          navigate={navigate}
          onOpenDrawer={handleOpenDrawer}
          onCenterVehicle={handleCenterOnVehicle}
          isTracked={isTracked}
          deviceTypeTab={deviceTypeTab}
          setDeviceTypeTab={setDeviceTypeTab}
        />

        {/* Right Panel Map Console */}
        <FleetMap
          isLoaded={isLoaded}
          center={center}
          zoom={zoom}
          isTracked={isTracked}
          trafficMode={trafficMode}
          routesMode={routesMode}
          liveMode={liveMode}
          vehicles={isTracked ? trackedVehicles : vehicles}
          selectedIds={selectedIds}
          activeMarker={activeMarker}
          setActiveMarker={setActiveMarker}
          handleZoomIn={handleZoomIn}
          handleZoomOut={handleZoomOut}
          handleReset={handleReset}
          setLiveMode={setLiveMode}
          setTrafficMode={setTrafficMode}
          setRoutesMode={setRoutesMode}
          onLoad={onLoad}
          onUnmount={onUnmount}
          getMarkerIcon={getMarkerIcon}
          navigate={navigate}
        />

        {/* Slide-out details drawer */}
        <FleetDrawer
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          vehicle={drawerVehicle}
          detail={drawerDetail}
          detailLoading={drawerDetailLoading}
          navigate={navigate}
          deviceTypeTab={deviceTypeTab}
        />
      </div>
    </div>
  );
};

export default FleetMonitoring;
